<template>
  <!-- ─────────────────────────────────────────────────────────────────────── -->
  <!--  RichTextBoxV2                                                          -->
  <!--  A contenteditable rich-text input with variable badges, emoji picker  -->
  <!--  and character counting.  Fully v-model compatible.                    -->
  <!-- ─────────────────────────────────────────────────────────────────────── -->
  <div class="rtb-wrapper">

    <!-- Main editor box -->
    <div
      class="rtb-box ion-padding"
      v-bind="attrs"
      :class="{
        'rtb-box--invalid':   validationState.isInvalid,
        'rtb-box--disabled':  disabled,
        'rtb-box--readonly':  readonly,
      }"
    >

      <!-- ── Editable area ─────────────────────────────────────────────── -->
      <div class="rtb-editor-container" @click="focusEditor">

        <!-- The actual contenteditable div -->
        <div
          ref="editorRef"
          class="rtb-editor"
          :contenteditable="isEditable ? 'true' : 'false'"
          @input="onInput"
          @keydown="onKeyDown"
          @paste="onPaste"
          @blur="onBlur"
          @focus="onFocus"
          @mouseup="saveSelection"
          @keyup="saveSelection"
        />

        <!-- Ghost placeholder (shown only when editor is empty) -->
        <div
          v-if="showPlaceholder"
          class="rtb-placeholder"
          aria-hidden="true"
          @click="focusEditor"
        >
          {{ placeholder }}
        </div>

      </div>

      <!-- ── Footer: toolbar + char count ──────────────────────────────── -->
      <div class="rtb-footer">

        <!-- ── Variable insertion ($ key or toolbar icon) ──────────────── -->
        <div
          v-if="resolvedShowVariableInsertion"
          class="rtb-toolbar-btn popover-wrapper"
          :class="{ 'rtb-toolbar-btn--disabled': !isEditable }"
        >
          <VIcon
            ref="variableBtnRef"
            name="curly-brackets-line"
            title="Insert variable (or press $)"
            @mousedown.prevent
            @click="openVariablePopover($event)"
          />

          <!--
            Variable list popover. It is promoted into the browser's native top
            layer by <TopLayer> rather than teleported to <body>: teleporting
            moves it out of an enclosing Vivid modal dialog, which both paints
            above all normal-flow content and marks everything outside itself
            `inert`. Staying in place keeps it a DOM descendant of the dialog
            (so it is interactive) while the popover host carries it into the
            top layer (so it paints on top). Being in the top layer also makes
            its `position: fixed` coordinates resolve against the viewport even
            when an ancestor has a transform or clips its overflow.
          -->
          <TopLayer :visible="popoverState.variableOpen">
            <div v-if="popoverState.variableOpen" class="rtb-popover-backdrop" @click="closeVariablePopover"></div>
            <div v-if="popoverState.variableOpen" ref="variablePanelRef" class="rtb-popover-panel rtb-variable-popup-panel" :style="popoverStyles.variable" @mousedown.prevent>
              <div class="rtb-variable-list">

                <!-- Built-in variables -->
                <div
                  v-for="item in builtInVariables"
                  :key="item.key"
                  class="rtb-variable-list__item"
                  @click="onVariableSelected(item.key)"
                >
                  {{ item.label }}
                </div>

                <!-- Divider shown only when there are both built-in and custom options -->
                <div
                  v-if="builtInVariables.length && (resolvedShowCustomField || resolvedShowBotVariable)"
                  class="rtb-variable-list__divider"
                />

                <!-- Custom Field trigger -->
                <div
                  v-if="resolvedShowCustomField"
                  ref="customFieldBtnRef"
                  class="rtb-variable-list__item rtb-variable-list__item--action"
                  @click="openCustomFieldModal"
                >
                  Custom Field…
                </div>

                <!-- Bot Variable trigger -->
                <div
                  v-if="resolvedShowBotVariable"
                  ref="botFieldBtnRef"
                  class="rtb-variable-list__item rtb-variable-list__item--action"
                  @click="openBotVariableModal"
                >
                  Bot Variable…
                </div>

              </div>
            </div>
          </TopLayer>
        </div>

        <!-- ── Emoji picker ──────────────────────────────────────────────── -->
        <div
          v-if="resolvedShowEmoji"
          class="rtb-toolbar-btn popover-wrapper"
          :class="{ 'rtb-toolbar-btn--disabled': !isEditable }"
        >
          <VIcon
            ref="emojiBtnRef"
            name="emoji-line"
            title="Insert emoji"
            @mousedown.prevent
            @click="openEmojiPopover($event)"
          />

          <Teleport to="body">
            <div v-if="popoverState.emojiOpen" class="rtb-popover-backdrop" @click="closeEmojiPopover"></div>
            <div v-if="popoverState.emojiOpen" ref="emojiPanelRef" class="rtb-popover-panel rtb-emoji-popup-panel" :style="popoverStyles.emoji" @mousedown.prevent>
              <EmojiPicker
                :native="true"
                :disable-skin-tones="true"
                :display-recent="false"
                :hide-search="false"
                :disable-footer="true"
                :show-preview="false"
                :show-categories="true"
                @select="onEmojiSelected"
              />
            </div>
          </Teleport>
        </div>

        <!-- ── Character counter (shows remaining / max, matching v1 behaviour) ── -->
        <div
          v-if="resolvedShowCharCount && maxLength"
          class="rtb-char-count"
          :class="{ 'rtb-char-count--over': remainingChars < 0 }"
          aria-live="polite"
        >
          {{ remainingChars }}&thinsp;/&thinsp;{{ maxLength }}
        </div>

      </div><!-- /rtb-footer -->

    </div><!-- /rtb-box -->

    <!-- Validation error message -->
    <div
      v-if="validationState.error"
      class="rtb-validation-error"
      role="alert"
    >
      {{ validationState.error }}
    </div>

    <!-- ── Modals (rendered outside the box to avoid z-index issues) ────── -->
    <template v-if="modalState.customFieldOpen">
      <CustomDataFieldPickerModal
        ref="customFieldModalRef"
        v-model:isOpen="modalState.customFieldOpen"
        can-add-new
        @selected="onVariableSelected"
      />
    </template>

    <template v-if="modalState.botVariableOpen">
      <BotVariablePickerModal
        ref="botVariableModalRef"
        :bot_id="resolvedBotId"
        v-model:isOpen="modalState.botVariableOpen"
        :formatted-output="true"
        @selected="onVariableSelected"
      />
    </template>

  </div><!-- /rtb-wrapper -->
</template>

<script setup>
/**
 * RichTextBoxV2
 *
 * An enhanced contenteditable rich-text input component supporting:
 *  - v-model (modelValue) two-way binding
 *  - Variable badges inserted via toolbar icon or the $ key
 *  - Emoji picker
 *  - Character counting where each badge = 1 character
 *  - placeholder / readonly / disabled props
 *  - Optional JSON content-type rendering
 *  - Validation (required field, angle-bracket integrity)
 */

import {
  ref,
  reactive,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
  useAttrs,
} from 'vue';
import { useUIStore } from '@/stores/ui';


import EmojiPicker from 'vue3-emoji-picker';

import CustomDataFieldPickerModal from './CustomDataFieldPickerModal.vue';
import BotVariablePickerModal from './BotVariablePickerModal.vue';
import TopLayer from './TopLayer.vue';
import { useRichTextEditor } from '../composables/useRichTextEditor.js';

// ─────────────────────────────────────────────────────────────────────────────
// Component meta
// ─────────────────────────────────────────────────────────────────────────────

defineOptions({ name: 'RichTextBoxV2', inheritAttrs: false });

const attrs = useAttrs();

// ─────────────────────────────────────────────────────────────────────────────
// Store (for toast notifications)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Shows a toast notification.
 * @param {{ message: string, type: string }} options
 */
function showToast({ message, type = 'warning' }) {
  useUIStore().updateToast({ open: true, message, type, duration: 5000 });
}

// ─────────────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────────────

const props = defineProps({
  /**
   * The bound value.  Use v-model="myVar" or v-model:modelValue="myVar".
   * Also accepts the legacy `value` prop for backwards compatibility.
   */
  modelValue: {
    type: String,
    default: '',
  },
  /** @deprecated Use modelValue / v-model instead */
  value: {
    type: String,
    default: '',
  },

  /**
   * List of built-in variable options shown in the variable popover.
   * Each item: { key: '<token>', label: 'Display Name' }
   */
  variables: {
    type: Array,
    default: () => [],
  },

  /** Show the variable insertion toolbar icon */
  showVariableInsertion: {
    type: Boolean,
    default: false,
  },

  /** Show the "Custom Field" option inside the variable popover */
  showCustomField: {
    type: Boolean,
    default: false,
  },

  /** Show the "Bot Variable" option inside the variable popover */
  showBotVariable: {
    type: Boolean,
    default: false,
  },

  /** Show the emoji picker toolbar icon */
  showEmoji: {
    type: Boolean,
    default: false,
  },

  /** Show the character counter in the footer */
  showCharCount: {
    type: Boolean,
    default: true,
  },

  /** Maximum number of logical characters allowed (badges count as 1 each) */
  maxLength: {
    type: Number,
    default: 1000,
  },

  /** Placeholder text shown when the editor is empty */
  placeholder: {
    type: String,
    default: 'Type here…',
  },

  /** Makes the editor read-only (no editing, but selectable text) */
  readonly: {
    type: Boolean,
    default: false,
  },

  /** Fully disables the editor (no editing, no interaction) */
  disabled: {
    type: Boolean,
    default: false,
  },

  /** Bot ID forwarded to BotVariablePickerModal */
  botId: {
    type: String,
    default: '',
  },

  /**
   * Content type:
   *  'TEXT' – plain text with variable tokens
   *  'JSON' – JSON string rendered with pretty-print and badge support
   */
  contentType: {
    type: String,
    default: 'TEXT',
    validator: (v) => ['TEXT', 'JSON'].includes(v),
  },

  /** Mark the field as required (triggers validation on blur) */
  required: {
    type: Boolean,
    default: false,
  },

  /**
   * Extra character buffer reserved when checking if a variable badge can be
   * inserted.  Useful when the API expands short tokens into longer strings
   * at send time.  Set to 0 to disable.
   */
  requiredBufferToAdd: {
    type: Number,
    default: 0,
  },

  // ── Backward-compatible prop aliases (deprecated — prefer the new names) ─────

  /** @deprecated Use showVariableInsertion instead */
  showCustomFiledInsertion: { type: Boolean, default: false },

  /** @deprecated Use showEmoji instead */
  showSmiley: { type: Boolean, default: false },

  /** @deprecated Use showCharCount instead */
  showRemainingText: { type: Boolean, default: false },

  /** @deprecated Use botId instead */
  // eslint-disable-next-line vue/prop-name-casing
  bot_id: { type: String, default: '' },
});

// ─────────────────────────────────────────────────────────────────────────────
// Emits
// ─────────────────────────────────────────────────────────────────────────────

const emit = defineEmits([
  /** Emitted when the value changes — enables v-model support */
  'update:modelValue',
  /** @deprecated Backward-compatible alias for update:modelValue (matches v1 rich-text-box) */
  'update:value',
  /** Convenience alias for frameworks expecting `input` */
  'input',
  /** Emitted on blur with the decoded value */
  'blur',
  /** Forwarded keydown event */
  'keydown',
  /** Forwarded paste event */
  'paste',
  /** Emitted when a variable badge is added; payload: variableToken */
  'add-variable',
  /** @deprecated Backward-compatible alias for add-variable (matches v1 rich-text-box) */
  'addParams',
  /** Emitted when a variable badge is removed; payload: variableToken */
  'remove-variable',
  /** @deprecated Backward-compatible alias for remove-variable (matches v1 rich-text-box) */
  'removeParams',
]);

// ─────────────────────────────────────────────────────────────────────────────
// Template refs
// ─────────────────────────────────────────────────────────────────────────────

const editorRef           = ref(null);
const emojiBtnRef         = ref(null);
const variableBtnRef      = ref(null);
const customFieldBtnRef   = ref(null);
const botFieldBtnRef      = ref(null);
const customFieldModalRef = ref(null);
const botVariableModalRef = ref(null);
const variablePanelRef    = ref(null);
const emojiPanelRef       = ref(null);

// ─────────────────────────────────────────────────────────────────────────────
// Composable
// ─────────────────────────────────────────────────────────────────────────────

const {
  saveSelection,
  restoreSelection,
  insertHtmlAtCursor,
  insertVariableBadge,
  removeBadgeById,
  extractPlainText,
  computeCharacterCount,
  initialiseEditorContent,
  validateContent,
  sanitiseHtml,
} = useRichTextEditor(editorRef, props);

// ─────────────────────────────────────────────────────────────────────────────
// Internal state
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Tracks the raw innerHTML of the editor.
 * This is the single source of truth for everything derived (charCount, value).
 */
const editorHtml = ref('');

/** Flag: prevents the external-value watcher from overwriting in-progress input */
const isUserEditing = ref(false);

/** Last emitted decoded value — used to avoid redundant emits */
const lastEmittedValue = ref('');

/** Last blurred decoded value — used to avoid redundant blur emits */
const lastBlurredValue = ref('');

/** Tracks whether the user has ever focused this editor (drives click-outside blur logic) */
const hasInteracted = ref(false);

/** ID of the pending blur setTimeout — stored so it can be cancelled when the editor regains focus. */
const blurTimerId = ref(null);
/** ID of the post-variable-selection refocus timer — cleared on unmount to prevent callbacks after teardown. */
const refocusTimerId = ref(null);

/** Popover open/event state */
const popoverState = reactive({
  variableOpen: false,
  variableEvent: null,
  emojiOpen: false,
  emojiEvent: null,
});

/** Popover position styles (fixed positioning) */
const popoverStyles = reactive({
  variable: {},
  emoji: {},
});

/** Modal open state */
const modalState = reactive({
  customFieldOpen: false,
  botVariableOpen: false,
});

/** Validation state (error message + invalid flag) */
const validationState = reactive({
  isInvalid: false,
  error: '',
});

// ─────────────────────────────────────────────────────────────────────────────
// Computed
// ─────────────────────────────────────────────────────────────────────────────

/** Whether the editor should accept input */
const isEditable = computed(() => !props.readonly && !props.disabled);

/**
 * Logical character count.
 * Each variable badge = 1, each <br> = 1, everything else at face value.
 */
const charCount = computed(() => computeCharacterCount(editorHtml.value));

/**
 * Remaining characters.  Can be negative if over the limit.
 */
const remainingChars = computed(() => props.maxLength - charCount.value);

/**
 * Show the placeholder overlay when the editor has no meaningful content.
 */
const showPlaceholder = computed(() => {
  if (props.readonly) return false;
  if (!editorHtml.value || editorHtml.value === '') return true;

  const temp = document.createElement('div');
  temp.innerHTML = editorHtml.value;

  // Remove badge elements for this check
  temp.querySelectorAll('[data-variable-badge="true"]').forEach((el) => el.remove());

  const textContent = temp.textContent ?? temp.innerText ?? '';
  const hasOnlyBreaks = editorHtml.value.replace(/<br\s*\/?>/gi, '').trim() === '';

  // Show placeholder only if there is no visible text AND no non-break content
  return textContent.length === 0 && hasOnlyBreaks;
});

/**
 * The built-in variable list.
 * Falls back to a sensible default set when the `variables` prop is empty.
 */
const builtInVariables = computed(() => {
  if (props.variables && props.variables.length > 0) return props.variables;
  return [
    { key: '<userfirstname>',              label: "User's First Name" },
    { key: '<userlastname>',               label: "User's Last Name" },
    { key: '<userfirstname> <userlastname>', label: "User's Full Name" },
    { key: '<userphone>',                  label: "User's Mobile Number" },
    { key: '<useremail>',                  label: "User's Email ID" },
  ];
});

/**
 * Resolved show-variable-insertion flag.
 * Accepts both the clean v2 prop name and the deprecated v1 name.
 */
const resolvedShowVariableInsertion = computed(
  () => props.showVariableInsertion || props.showCustomFiledInsertion,
);

/**
 * Resolved show-emoji flag.
 * Accepts both the clean v2 prop name and the deprecated v1 name.
 */
const resolvedShowEmoji = computed(
  () => props.showEmoji || props.showSmiley,
);

/**
 * Resolved show-char-count flag.
 * Accepts both the clean v2 prop name and the deprecated v1 name.
 */
const resolvedShowCharCount = computed(
  () => props.showCharCount || props.showRemainingText,
);

/**
 * Resolved bot ID.
 * Accepts both camelCase (v2) and snake_case (v1 legacy) prop.
 */
const resolvedBotId = computed(
  () => props.botId || props.bot_id,
);

/**
 * Resolved show-custom-field flag.
 * In v1, the Custom Field option was always shown whenever showCustomFiledInsertion
 * was true.  Maintain that behaviour for backward compatibility.
 */
const resolvedShowCustomField = computed(
  () => props.showCustomField || props.showCustomFiledInsertion,
);

/**
 * Resolved show-bot-variable flag.
 * In v1, showBotVariable defaulted to true (shown whenever the variable popover
 * was open).  Maintain that behaviour when showCustomFiledInsertion is used.
 */
const resolvedShowBotVariable = computed(
  () => (props.showBotVariable || props.showCustomFiledInsertion) && resolvedBotId.value,
);

// ─────────────────────────────────────────────────────────────────────────────
// Watchers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * React to external value changes (parent updates modelValue / value).
 * We only update the DOM when the editor is NOT currently being edited by the
 * user, to avoid fighting with the IME / composition events.
 */
watch(
  // Use `||` (not `??`) so an empty-string modelValue falls through to the
  // legacy `value` prop; otherwise the watcher never sees `value` changes and
  // async-loaded content (e.g. editing a WhatsApp template body) never renders.
  () => props.modelValue || props.value,
  (newVal) => {
    if (isUserEditing.value) return;
    if (!editorRef.value) return;

    // Only update when the editor is not focused (prevents cursor jump)
    if (document.activeElement !== editorRef.value) {
      editorHtml.value = initialiseEditorContent(newVal, props.contentType) ?? '';
    }
  },
);

// ─────────────────────────────────────────────────────────────────────────────
// Lifecycle
// ─────────────────────────────────────────────────────────────────────────────

onMounted(() => {
  // Initialise content from the initial prop value
  const initialValue = props.modelValue || props.value || '';
  editorHtml.value = initialiseEditorContent(initialValue, props.contentType) ?? '';

  // Delegate badge-remove click events (event delegation on the editor root)
  editorRef.value?.addEventListener('click', handleEditorClick);

  // Global mousedown to detect clicks outside the component
  document.addEventListener('mousedown', handleGlobalMouseDown, true);
});

onBeforeUnmount(() => {
  editorRef.value?.removeEventListener('click', handleEditorClick);
  document.removeEventListener('mousedown', handleGlobalMouseDown, true);
  window.removeEventListener('scroll', repositionOpenPopovers, true);
  window.removeEventListener('resize', repositionOpenPopovers);
  if (blurTimerId.value !== null) {
    clearTimeout(blurTimerId.value);
    blurTimerId.value = null;
  }
  if (refocusTimerId.value !== null) {
    clearTimeout(refocusTimerId.value);
    refocusTimerId.value = null;
  }
});

// ─────────────────────────────────────────────────────────────────────────────
// Editor event handlers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Handles all clicks inside the editor element.
 * Specifically intercepts clicks on badge-remove buttons.
 */
function handleEditorClick(event) {
  hasInteracted.value = true;

  const target = event.target;

  if (target.classList?.contains('rtb-badge-remove')) {
    const badgeId = target.getAttribute('data-badge-id');
    const removedToken = removeBadgeById(badgeId);
    if (removedToken !== null) {
      editorHtml.value = editorRef.value.innerHTML;
      emit('remove-variable', removedToken);
      emit('removeParams', removedToken.replace(/[<>]/g, '')); // backward-compat
      syncValue();
    }
  }
}

/**
 * Detects mousedown events outside the component.
 * Triggers a blur / validation cycle if the user previously interacted.
 */
function handleGlobalMouseDown(event) {
  const isInsideComponent = isNodeInsideComponent(event.target);
  if (!isInsideComponent && hasInteracted.value) {
    // Capture modal state BEFORE clearing — Ionic modals are rendered as
    // body-level portals so isNodeInsideComponent returns false for clicks
    // inside them.  We must not emit blur when the user is interacting with
    // a modal (selecting a custom field or bot variable).
    const wasModalOpen =
      modalState.customFieldOpen ||
      modalState.botVariableOpen;

    // Close all popovers/modals
    popoverState.variableOpen   = false;
    popoverState.emojiOpen      = false;
    modalState.customFieldOpen  = false;
    modalState.botVariableOpen  = false;

    if (!wasModalOpen) {
      // Genuine click outside the component — trigger blur normally.
      triggerBlur();
      hasInteracted.value = false;
    }
    // If a modal was open the user was selecting a variable; onVariableSelected
    // will call editorRef.focus() in nextTick which will cancel the pending
    // onBlur timer via onFocus, so no spurious blur is ever emitted.
  }
}

/**
 * Checks whether a DOM node is inside any of the component's relevant elements
 * (editor, buttons, popovers, modals).
 */
function isNodeInsideComponent(node) {
  const roots = [
    editorRef.value,
    emojiBtnRef.value,
    variableBtnRef.value,
    customFieldBtnRef.value,
    botFieldBtnRef.value,
    customFieldModalRef.value?.$el,
    botVariableModalRef.value?.$el,
  ];

  let el = node;
  while (el) {
    if (roots.includes(el)) return true;
    if (
      el.classList?.contains('rtb-variable-popover') ||
      el.classList?.contains('rtb-emoji-popover') ||
      el.classList?.contains('popover-panel') ||
      el.classList?.contains('popover-backdrop') ||
      el.classList?.contains('popover-wrapper') ||
      el.classList?.contains('rtb-popover-backdrop') ||
      el.classList?.contains('rtb-popover-panel') ||
      el.classList?.contains('rtb-variable-popup-panel') ||
      el.classList?.contains('rtb-emoji-popup-panel') ||
      el.classList?.contains('rtb-wrapper') ||
      el.classList?.contains('rtb-toolbar-btn') ||
      el.classList?.contains('rtb-variable-list') ||
      el.classList?.contains('v3-emoji-picker') ||
      el.classList?.contains('emoji-popup-panel') ||
      el.classList?.contains('variable-popup-panel')
    ) {
      return true;
    }
    el = el.parentElement;
  }
  return false;
}

/** Returns the data-value of every badge present in an HTML string. */
function getBadgeTokens(html) {
  const div = document.createElement('div');
  div.innerHTML = html;
  return [...div.querySelectorAll('[data-variable-badge="true"]')]
    .map(el => el.getAttribute('data-value') ?? '');
}

/**
 * Handles `input` events from the contenteditable div.
 * Normalises div/block elements created by the browser into <br> tags for
 * consistent line-break handling.
 */
function onInput(event) {
  isUserEditing.value = true;

  let html = event.target.innerHTML;

  // Normalise browser-generated <div> wrappers around lines
  html = html.replace(/<div><br><\/div>/gi, '<br>');
  html = html.replace(/<div>([^<]*)<\/div>/gi, '<br>$1');
  html = html.replace(/<div><\/div>/gi, '<br>');

  // If the normalisation changed anything, update innerHTML and restore cursor
  if (event.target.innerHTML !== html) {
    const sel     = window.getSelection();
    const range   = sel?.rangeCount > 0 ? sel.getRangeAt(0) : null;

    event.target.innerHTML = html;

    // Try to restore the cursor to where it was
    if (range && event.target.lastChild) {
      try {
        const newRange = document.createRange();
        newRange.setStart(event.target.lastChild, 0);
        newRange.collapse(true);
        sel?.removeAllRanges();
        sel?.addRange(newRange);
      } catch (_) {
        // Cursor restore failed – not critical
      }
    }
  }

  // Sanitise the normalised HTML before storing — DOMPurify removes XSS vectors
  // while preserving our badge spans and <br> tags.
  let sanitised = sanitiseHtml(html);

  // Browsers often leave a lone <br> behind after "select all + delete"
  // (e.g. Ctrl+A, Delete/Backspace). That phantom line-break has no visible
  // content but still counts as 1 character, permanently stealing a slot from
  // maxLength. Collapse it to a true empty string when there is no other text
  // or badge content, matching the emptiness check used by showPlaceholder.
  let collapsedToEmpty = false;
  if (sanitised && sanitised.replace(/<br\s*\/?>/gi, '').trim() === '') {
    const probe = document.createElement('div');
    probe.innerHTML = sanitised;
    if (!probe.querySelector('[data-variable-badge="true"]')) {
      sanitised = '';
      collapsedToEmpty = true;
    }
  }

  // Emit removal events for badges the browser deleted without going through
  // handleBackspace (e.g. badge at end of editor, two adjacent badges, Delete key,
  // select-all-delete). Safe to call even if handleBackspace already emitted,
  // because it updated editorHtml.value first — so the diff would show no change.
  const prevTokens = getBadgeTokens(editorHtml.value);
  const nextTokens = getBadgeTokens(sanitised);
  for (const token of new Set(prevTokens)) {
    const removed = prevTokens.filter(t => t === token).length
                  - nextTokens.filter(t => t === token).length;
    for (let i = 0; i < removed; i++) {
      emit('remove-variable', token);
      emit('removeParams', token.replace(/[<>]/g, '')); // backward-compat
    }
  }

  editorHtml.value = sanitised;
  if (collapsedToEmpty && event.target.innerHTML !== sanitised) {
    event.target.innerHTML = sanitised;
  }
  syncValue();

  // Reset the flag on next tick so watchers can resume
  nextTick(() => { isUserEditing.value = false; });
}

/**
 * Handles keydown events.
 *  - Enter → insert <br> (not a new <div>)
 *  - Backspace → handle badge removal
 *  - $ → open the variable popover
 *  - Enforce maxLength
 */
function onKeyDown(event) {
  if (event.key === 'Enter') {
    event.preventDefault();
    insertLineBreak();
    editorHtml.value = editorRef.value.innerHTML;
    syncValue();
    return;
  }

  if (event.key === 'Backspace') {
    const badgeWasRemoved = handleBackspace();
    // Only prevent default when we manually handled a badge removal;
    // for normal character deletion we let the browser do its thing.
    if (badgeWasRemoved) event.preventDefault();
    return;
  }

  // $$ triggers variable insertion.
  // The first $ is typed normally; the second $ detects the preceding $ in the
  // text node, removes both characters, and opens the variable popover.
  if (event.key === '$' && resolvedShowVariableInsertion.value && isEditable.value) {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      const node  = range.startContainer;
      const offset = range.startOffset;
      if (node.nodeType === Node.TEXT_NODE && offset > 0 && node.textContent[offset - 1] === '$') {
        event.preventDefault();
        // Delete the preceding '$' so neither dollar sign remains in the editor.
        const delRange = document.createRange();
        delRange.setStart(node, offset - 1);
        delRange.setEnd(node, offset);
        delRange.deleteContents();
        sel.removeAllRanges();
        sel.addRange(delRange);
        saveSelection();
        openVariablePopover(event);
        return;
      }
    }
    // First '$' — fall through and let the browser insert it normally.
  }

  // Block input when at or over the character limit
  if (
    props.maxLength &&
    charCount.value >= props.maxLength &&
    !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'Tab'].includes(event.key) &&
    !event.ctrlKey && !event.metaKey
  ) {
    event.preventDefault();
    showToast({ message: 'Maximum character length reached.', type: 'warning' });
    return;
  }

  emit('keydown', event);
}

/**
 * Handles paste events.
 * Strips all rich-text formatting and pastes plain text only.
 * Converts line-breaks to <br> tags.
 * Respects the maxLength limit.
 */
function onPaste(event) {
  event.preventDefault();
  if (!isEditable.value) return;
  const pastedText = event.clipboardData?.getData('text/plain') ?? '';
  if (!pastedText) return;

  const available = props.maxLength ? props.maxLength - charCount.value : Infinity;
  if (available <= 0) {
    showToast({ message: 'Maximum character length reached.', type: 'warning' });
    return;
  }

  const trimmedText = props.maxLength ? pastedText.slice(0, available) : pastedText;

  // Convert all line-break variants to <br>
  const htmlText = trimmedText
    .replace(/\r\n/g, '<br>')
    .replace(/\r/g, '<br>')
    .replace(/\n/g, '<br>');

  insertHtmlAtCursor(htmlText);
  editorHtml.value = editorRef.value.innerHTML;
  syncValue();

  emit('paste', event);
}

/** Handles focus events on the editor — cancels any pending blur timer. */
function onFocus() {
  // If the editor regains focus (e.g. after variable insertion from a modal),
  // cancel the pending blur so configureNode / @blur handlers are not called.
  if (blurTimerId.value !== null) {
    clearTimeout(blurTimerId.value);
    blurTimerId.value = null;
  }
  hasInteracted.value = true;
}

/** Handles blur events from the editor element itself (e.g. Tab key). */
function onBlur() {
  // Store the timeout ID so onFocus can cancel it if the editor regains focus
  // before the delay expires (e.g. after variable/emoji insertion).
  blurTimerId.value = setTimeout(() => {
    blurTimerId.value = null;
    triggerBlur();
  }, 150);
}

/** Focuses the editor programmatically. */
function focusEditor() {
  if (!isEditable.value) return;
  editorRef.value?.focus();
  hasInteracted.value = true;
  syncValue();
}

// ─────────────────────────────────────────────────────────────────────────────
// Popover placement
// ─────────────────────────────────────────────────────────────────────────────

/** Gap between a popover panel and its trigger / the viewport edge, in px. */
const POPOVER_MARGIN = 8;

/**
 * Applied while the panel is rendered but not yet measured, so it is never
 * painted at the un-positioned default spot for a frame.
 */
const HIDDEN_POPOVER_STYLE = Object.freeze({ visibility: 'hidden' });

/**
 * Places a `position: fixed` popover panel against its trigger.
 *
 * Preferred placement is above the trigger, right edge aligned with the
 * trigger's right edge (the long-standing look of this component). The panel
 * is then kept inside the viewport: it flips below the trigger when there is
 * not enough room above, and is clamped horizontally. Without the clamping a
 * tall panel on a trigger near the top of the page was positioned off the top
 * of the screen and was therefore invisible and unclickable.
 *
 * `top`/`left` are used rather than `bottom`/`right` so the result can be
 * clamped against both edges in one place.
 *
 * @param {HTMLElement|null} triggerEl
 * @param {HTMLElement|null} panelEl
 * @returns {Record<string, string>} inline style object
 */
function placePopover(triggerEl, panelEl) {
  if (!triggerEl || !panelEl) return {};

  const trigger = triggerEl.getBoundingClientRect();
  const width  = panelEl.offsetWidth;
  const height = panelEl.offsetHeight;
  const viewportWidth  = window.innerWidth;
  const viewportHeight = window.innerHeight;

  // Vertical: above the trigger if it fits, otherwise below, otherwise clamped.
  let top = trigger.top - POPOVER_MARGIN - height;
  if (top < POPOVER_MARGIN) {
    const below = trigger.bottom + POPOVER_MARGIN;
    top = below + height + POPOVER_MARGIN <= viewportHeight
      ? below
      : Math.max(POPOVER_MARGIN, viewportHeight - height - POPOVER_MARGIN);
  }

  // Horizontal: right-aligned to the trigger, clamped to the viewport.
  const maxLeft = Math.max(POPOVER_MARGIN, viewportWidth - width - POPOVER_MARGIN);
  const left = Math.min(Math.max(POPOVER_MARGIN, trigger.right - width), maxLeft);

  return { top: `${top}px`, left: `${left}px` };
}

/**
 * Re-places whichever popover is open. Bound to scroll (capture phase, so it
 * also catches inner scroll containers such as the broadcast edit pane) and to
 * resize while a popover is open — a `position: fixed` panel does not move with
 * its trigger otherwise.
 */
function repositionOpenPopovers() {
  if (popoverState.variableOpen) positionVariablePopover();
  if (popoverState.emojiOpen) positionEmojiPopover();
}

watch(
  () => popoverState.variableOpen || popoverState.emojiOpen,
  (anyOpen) => {
    if (anyOpen) {
      window.addEventListener('scroll', repositionOpenPopovers, true);
      window.addEventListener('resize', repositionOpenPopovers);
    } else {
      window.removeEventListener('scroll', repositionOpenPopovers, true);
      window.removeEventListener('resize', repositionOpenPopovers);
    }
  },
);

// ─────────────────────────────────────────────────────────────────────────────
// Variable insertion
// ─────────────────────────────────────────────────────────────────────────────

function openVariablePopover(event) {
  if (!isEditable.value) return;
  saveSelection();
  popoverState.variableEvent = event;

  // Render hidden first so the panel has a measurable size, then place it.
  popoverStyles.variable = HIDDEN_POPOVER_STYLE;
  popoverState.variableOpen = true;
  nextTick(positionVariablePopover);
}

function positionVariablePopover() {
  if (!popoverState.variableOpen) return;
  const btn = variableBtnRef.value?.$el || variableBtnRef.value;
  popoverStyles.variable = placePopover(btn, variablePanelRef.value);
}

function closeVariablePopover() {
  popoverState.variableOpen = false;
  popoverState.variableEvent = null;
}

function openCustomFieldModal() {
  closeVariablePopover();
  modalState.customFieldOpen = true;
}

function openBotVariableModal() {
  closeVariablePopover();
  modalState.botVariableOpen = true;
}

/**
 * Called when the user picks a variable from any source
 * (built-in list, custom field modal, bot variable modal).
 *
 * @param {string} variableKey – e.g. "<userfirstname>" or "my_custom_field"
 */
function onVariableSelected(variableKey) {
  closeVariablePopover();
  modalState.customFieldOpen = false;
  modalState.botVariableOpen = false;

  if (!variableKey) return;

  // Guard: badge counts as 1 character + the optional API-expansion buffer.
  if (props.maxLength && charCount.value + 1 + props.requiredBufferToAdd > props.maxLength) {
    showToast({ message: 'Maximum character length reached.', type: 'warning' });
    return;
  }

  // insertVariableBadge handles normalisation of the key
  insertVariableBadge(variableKey);

  editorHtml.value = sanitiseHtml(editorRef.value.innerHTML);
  isUserEditing.value = true;
  syncValue();

  const variableDisplayName = variableKey.replace(/[<>]/g, '');
  emit('add-variable', variableKey);
  emit('addParams', variableDisplayName); // backward-compat with v1

  nextTick(() => {
    editorRef.value?.focus();
    isUserEditing.value = false;
  });

  // Ionic modal close animations take ~250 ms.  After the animation the Ionic
  // framework restores focus to the last active element before the modal
  // opened, which makes the caret disappear.  Re-focus the editor and restore
  // the saved cursor range (set by insertHtmlAtCursor to just after the badge)
  // once the animation is guaranteed to have finished.
  refocusTimerId.value = setTimeout(() => {
    refocusTimerId.value = null;
    if (document.activeElement !== editorRef.value) {
      editorRef.value?.focus();
      restoreSelection();
    }
  }, 350);
}

// ─────────────────────────────────────────────────────────────────────────────
// Emoji insertion
// ─────────────────────────────────────────────────────────────────────────────

function openEmojiPopover(event) {
  if (!isEditable.value) return;
  saveSelection();
  popoverState.emojiEvent = event;

  // Render hidden first so the panel has a measurable size, then place it.
  popoverStyles.emoji = HIDDEN_POPOVER_STYLE;
  popoverState.emojiOpen = true;
  nextTick(positionEmojiPopover);
}

function positionEmojiPopover() {
  if (!popoverState.emojiOpen) return;
  const btn = emojiBtnRef.value?.$el || emojiBtnRef.value;
  popoverStyles.emoji = placePopover(btn, emojiPanelRef.value);
}

function closeEmojiPopover() {
  popoverState.emojiOpen = false;
  popoverState.emojiEvent = null;
}

/**
 * Called when the user selects an emoji from the picker.
 * @param {{ i: string }} emoji – vue3-emoji-picker selection object
 */
function onEmojiSelected(emoji) {
  const emojiChar   = emoji.i;
  const emojiLength = [...emojiChar].length; // handle multi-codepoint emoji

  if (props.maxLength) {
    const available = props.maxLength - charCount.value;
    if (available <= 0) {
      showToast({ message: 'Maximum character length reached.', type: 'warning' });
      return;
    }
    if (emojiLength > available) {
      showToast({
        message: `Only ${available} character${available !== 1 ? 's' : ''} remaining.`,
        type: 'warning',
      });
      return;
    }
  }

  insertHtmlAtCursor(emojiChar);
  editorHtml.value = sanitiseHtml(editorRef.value.innerHTML);
  isUserEditing.value = true;
  syncValue();
  closeEmojiPopover();

  nextTick(() => {
    editorRef.value?.focus();
    isUserEditing.value = false;
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Keyboard helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Inserts a <br> element at the cursor position (Enter key behaviour). */
function insertLineBreak() {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return;

  const range = sel.getRangeAt(0);
  const br = document.createElement('br');
  range.deleteContents();
  range.insertNode(br);

  // Move cursor after the <br>
  range.setStartAfter(br);
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
}

/**
 * Handles the Backspace key for badge removal.
 *
 * Returns true when a badge was manually removed (caller must call
 * event.preventDefault() to prevent the browser also deleting a character).
 * Returns false to let the browser handle normal character deletion.
 */
function handleBackspace() {
  if (!isEditable.value) return false;

  const sel = window.getSelection();
  if (!sel || !sel.anchorNode) return false;

  const node = sel.anchorNode;

  // Case 1: Cursor is directly on/inside a badge element (contenteditable=false).
  if (node.nodeType === Node.ELEMENT_NODE && node.getAttribute?.('data-variable-badge')) {
    const removedToken = node.getAttribute('data-value');
    node.remove();
    editorHtml.value = editorRef.value.innerHTML;
    if (removedToken !== null) {
      emit('remove-variable', removedToken);
      emit('removeParams', removedToken.replace(/[<>]/g, '')); // backward-compat
      syncValue();
    }
    return true;
  }

  // Case 2: Cursor is at the very start of a text node immediately after a badge.
  if (
    node.nodeType === Node.TEXT_NODE &&
    sel.anchorOffset === 0 &&
    node.previousSibling?.getAttribute?.('data-variable-badge')
  ) {
    const removedToken = node.previousSibling.getAttribute('data-value');
    node.previousSibling.remove();
    editorHtml.value = editorRef.value.innerHTML;
    if (removedToken !== null) {
      emit('remove-variable', removedToken);
      emit('removeParams', removedToken.replace(/[<>]/g, '')); // backward-compat
      syncValue();
    }
    return true;
  }

  // Default: let the browser handle normal character deletion.
  return false;
}

// ─────────────────────────────────────────────────────────────────────────────
// Value synchronisation & validation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Runs validation, then emits the decoded value to the parent if it changed.
 */
function syncValue() {
  const { valid, error } = runValidation();

  if (!valid) {
    validationState.isInvalid = true;
    validationState.error     = error;
    // Do not emit an invalid value upward
    return;
  }

  // Clear previous validation errors
  validationState.isInvalid = false;
  validationState.error     = '';

  const decoded = extractPlainText(editorHtml.value, props.contentType);

  if (decoded !== lastEmittedValue.value) {
    lastEmittedValue.value = decoded;
    emit('update:modelValue', decoded);
    emit('update:value', decoded); // backward-compat with v1 rich-text-box
    emit('input', decoded);
  }
}

/**
 * Runs validation and emits blur if the value changed since the last blur.
 * Skips emission when any popover or modal is currently open — this prevents
 * spurious blur calls when the user presses $ to open the variable picker or
 * when a variable/emoji selection closes the overlay.
 */
function triggerBlur() {
  // If any popover or modal is open, the user is still interacting with the
  // component — do not emit blur.
  if (
    popoverState.variableOpen ||
    popoverState.emojiOpen    ||
    modalState.customFieldOpen ||
    modalState.botVariableOpen
  ) return;

  const { valid, error } = runValidation();

  validationState.isInvalid = !valid;
  validationState.error     = error;

  if (!valid) return;

  const decoded = extractPlainText(editorHtml.value, props.contentType);
  if (decoded !== lastBlurredValue.value) {
    lastBlurredValue.value = decoded;
    emit('blur', decoded);
  }
}

/** Delegates to the composable's validateContent helper. */
function runValidation() {
  const decoded = extractPlainText(editorHtml.value, props.contentType);
  return validateContent(decoded, props.contentType, props.required);
}

// ─────────────────────────────────────────────────────────────────────────────
// Public methods (exposed for parent refs)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Triggers validation and returns the result.
 * Useful for parent form components that call validate() on submit.
 * @returns {{ valid: boolean, error: string }}
 */
function validate() {
  const result = runValidation();
  validationState.isInvalid = !result.valid;
  validationState.error     = result.error;
  return result;
}

/** Returns the current decoded plain-text value. */
function getValue() {
  return extractPlainText(editorHtml.value, props.contentType);
}

/** Clears the editor content. */
function clear() {
  if (editorRef.value) editorRef.value.innerHTML = '';
  editorHtml.value       = '';
  lastEmittedValue.value = '';
  lastBlurredValue.value = '';
  validationState.isInvalid = false;
  validationState.error     = '';
  emit('update:modelValue', '');
  emit('input', '');
}

defineExpose({ validate, getValue, clear, focusEditor });
</script>

<style lang="scss" scoped>
/* ─────────────────────────────────────────────────────────────────────────────
   RichTextBoxV2 Styles
   Naming convention: BEM-style with rtb- prefix to avoid collisions
───────────────────────────────────────────────────────────────────────────── */

.rtb-wrapper {
  width: 100%;
}

/* ── Main box ──────────────────────────────────────────────────────────────── */
.rtb-box {
  border: 1px solid var(--ion-color-medium, #e0e0e0);
  border-radius: 8px;
  background: var(--background-primary, #ffffff);
  font-family: inherit;
  width: 100%;
  overflow: visible;
  position: relative;
  z-index: 1;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus-within {
    border-color: var(--ion-color-primary, #3880ff);
    box-shadow: 0 0 0 2px rgba(var(--ion-color-primary-rgb, 56, 128, 255), 0.15);
  }

  &--invalid {
    border-color: var(--ion-color-danger, #e74c3c) !important;
    box-shadow: 0 0 0 2px rgba(231, 76, 60, 0.12);
  }

  &--disabled {
    opacity: 0.55;
    pointer-events: none;
    background: var(--ion-color-light, #f5f5f5);
  }

  &--readonly {
    background: var(--ion-color-light, #f5f5f5);

    .rtb-editor {
      pointer-events: none;
      user-select: text;
    }
  }
}

/* ── Editor container & placeholder ─────────────────────────────────────────── */
.rtb-editor-container {
  position: relative;
  cursor: text;
}

.rtb-editor {
  min-height: 40px;
  outline: none;
  width: 100%;
  font-size: 1rem;
  line-height: 1.5;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.rtb-placeholder {
  position: absolute;
  inset: 0;
  color: var(--ion-color-medium, #999);
  font-size: 1rem;
  line-height: 1.5;
  pointer-events: none;
  user-select: none;
  display: flex;
  align-items: flex-start;
  padding-top: 2px;
  z-index: 1;
}

/* ── Footer ──────────────────────────────────────────────────────────────────── */
.rtb-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 8px;
  margin-top: 4px;
  border-top: 1px solid var(--gray-02);
  position: relative;
  z-index: 10;
}

/* ── Toolbar buttons (emoji / variable icons) ───────────────────────────────── */
.rtb-toolbar-btn {
  display: flex;
  align-items: center;
  cursor: pointer;
  color: var(--black);
  font-size: 14px;
  transition: color 0.15s ease;

  &--disabled {
    opacity: 0.4;
    pointer-events: none;
    cursor: not-allowed;
  }
}

/* ── Popover wrapper (button container) ─────────────────────────────────────── */
.popover-wrapper {
  position: relative;
}

/* ── Character counter ──────────────────────────────────────────────────────── */
.rtb-char-count {
  font-size: 14px;
  color: var(--black);
  min-width: 56px;
  text-align: right;

  &--over {
    color: var(--ion-color-danger, #e74c3c);
    font-weight: 600;
  }
}

/* ── Validation error message ───────────────────────────────────────────────── */
.rtb-validation-error {
  color: var(--ion-color-danger, #e74c3c);
  font-size: 0.85rem;
  margin-top: 4px;
}

/* ── Badge (variable pill) styles — deep to target dynamic DOM ──────────────── */
:deep(.rtb-badge) {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin: 0 2px;
  padding: 2px 8px 2px 8px;
  font-size: 0.85rem;
  font-weight: 500;
  line-height: 1.4;
  border-radius: 12px;
  background: var(--ion-color-primary, #6f42c1);
  color: #fff;
  user-select: none;
  cursor: default;
  vertical-align: middle;
  white-space: nowrap;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  position: relative;
  padding-right: 22px; // space for the × button
}

:deep(.rtb-badge-remove) {
  position: absolute;
  right: 5px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  opacity: 0.7;
  transition: opacity 0.1s ease;

  &:hover {
    opacity: 1;
  }
}
</style>

<!-- Global (non-scoped) styles for teleported popovers -->
<style lang="scss">
/* The <TopLayer> host that carries the variable popover is pointer-events: none
   so it can never become a full-viewport click blocker; its children opt back
   in. Harmless for the emoji popover, which is still teleported to <body>. */
.rtb-popover-backdrop {
  position: fixed;
  inset: 0;
  z-index: 99998;
  pointer-events: auto;
}
.rtb-popover-panel {
  position: fixed;
  z-index: 99999;
  background: var(--white, #fff);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  pointer-events: auto;
}
.rtb-emoji-popup-panel {
  min-width: 300px;
}
.rtb-variable-popup-panel {
  min-width: 220px;
}
.rtb-variable-list {
  padding: 4px 0;
}
.rtb-variable-list__item {
  padding: 10px 16px;
  font-size: 0.95rem;
  cursor: pointer;
  border-bottom: 1px solid var(--ion-color-light, #f0f0f0);
  transition: background 0.1s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--ion-color-light, #f5f5f5);
  }
}
.rtb-variable-list__item--action {
  color: var(--ion-color-primary, #3880ff);
  font-weight: 500;
}
.rtb-variable-list__divider {
  height: 1px;
  background: var(--ion-color-medium, #ccc);
  margin: 4px 0;
}
</style>
